/* eslint-disable no-restricted-syntax */
// Deprecated: kept for backward compatibility

const TRAP_FOCUS_HANDLERS = {};
const Utility = {
  _swatchesCache: null,

  /**
   * DOM Selection Utilities
   * Simplified methods for querying DOM elements
   */
  DomSelector: {
    /**
     * Query single element with optional parent context
     * @param {string} selector - CSS selector
     * @param {Element} context - Parent element (default: document)
     * @returns {Element|null}
     */
    $(selector, context = document) {
      return (context || document).querySelector(selector);
    },

    /**
     * Query multiple elements with optional parent context
     * @param {string} selector - CSS selector
     * @param {Element} context - Parent element (default: document)
     * @returns {Array}
     */
    $$(selector, context = document) {
      return Array.from((context || document).querySelectorAll(selector));
    },

    /**
     * Query element by ID
     * @param {string} id - Element ID (without #)
     * @returns {Element|null}
     */
    byId(id) {
      return document.getElementById(id);
    },

    /**
     * Check if element exists
     * @param {string} selector
     * @param {Element} context
     * @returns {boolean}
     */
    exists(selector, context = document) {
      return (context || document).querySelector(selector) !== null;
    }
  },

  /**
   * CSS Class Manipulation Utilities
   * Simplified methods for managing CSS classes on single or multiple elements
   */
  CSS: {
    /**
     * Add class to single or multiple elements
     * @param {Element|Element[]} elements - Single element or array of elements
     * @param {...string} classes - Classes to add
     * @example
     * Utility.CSS.add(element, 'open');
     * Utility.CSS.add([el1, el2], 'active', 'visible');
     */
    add(elements, ...classes) {
      if (!elements || classes.length === 0) return;
      const items = Array.isArray(elements) ? elements : [elements];
      items.forEach(el => el?.classList.add(...classes));
    },

    /**
     * Remove class from single or multiple elements
     * @param {Element|Element[]} elements - Single element or array of elements
     * @param {...string} classes - Classes to remove
     * @example
     * Utility.CSS.remove(element, 'open');
     * Utility.CSS.remove([el1, el2], 'active', 'visible');
     */
    remove(elements, ...classes) {
      if (!elements || classes.length === 0) return;
      const items = Array.isArray(elements) ? elements : [elements];
      items.forEach(el => el?.classList.remove(...classes));
    },

    /**
     * Toggle class on single or multiple elements
     * @param {Element|Element[]} elements - Single element or array of elements
     * @param {string} className - Class to toggle
     * @param {boolean} force - Optional: force add (true) or remove (false)
     * @example
     * Utility.CSS.toggle(element, 'open');
     * Utility.CSS.toggle(element, 'visible', true);
     */
    toggle(elements, className, force) {
      if (!elements || !className) return;
      const items = Array.isArray(elements) ? elements : [elements];
      items.forEach(el => el?.classList.toggle(className, force));
    },

    /**
     * Check if element has class
     * @param {Element} element - Element to check
     * @param {string} className - Class name
     * @returns {boolean}
     * @example
     * if (Utility.CSS.has(element, 'open')) { ... }
     */
    has(element, className) {
      return element?.classList.contains(className) || false;
    },

    /**
     * Replace one class with another
     * @param {Element|Element[]} elements - Single element or array of elements
     * @param {string} oldClass - Class to replace
     * @param {string} newClass - New class
     * @example
     * Utility.CSS.replace(element, 'loading', 'loaded');
     */
    replace(elements, oldClass, newClass) {
      if (!elements || !oldClass || !newClass) return;
      const items = Array.isArray(elements) ? elements : [elements];
      items.forEach(el => el?.classList.replace(oldClass, newClass));
    }
  },

  /**
   * IntersectionObserver Utilities
   * Simplified methods for observing element visibility
   */
  Observer: {
    /**
     * Create an IntersectionObserver with common defaults
     * @param {Function} callback - Function to call when intersection changes
     * @param {Object} options - IntersectionObserver options
     * @returns {IntersectionObserver}
     */
    create(callback, options = {}) {
      const defaultOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0,
        ...options
      };
      return new IntersectionObserver(callback, defaultOptions);
    },

    /**
     * Observe element once and disconnect after first intersection
     * @param {Element|string} element - Element or selector
     * @param {Function} onVisible - Callback when element becomes visible
     * @param {Object} options - Observer options
     * @returns {IntersectionObserver}
     */
    once(element, onVisible, options = {}) {
      const callback = (entries, observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            onVisible(entry.target, entry);
            observer.disconnect();
          }
        });
      };

      const observer = this.create(callback, options);
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (target) observer.observe(target);
      
      return observer;
    },

    /**
     * Observe element visibility changes (both enter and exit)
     * @param {Element|string} element - Element or selector
     * @param {Function} onEnter - Callback when element enters viewport
     * @param {Function} onExit - Callback when element exits viewport
     * @param {Object} options - Observer options
     * @returns {IntersectionObserver}
     */
    onChange(element, onEnter, onExit, options = {}) {
      const callback = (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && onEnter) {
            onEnter(entry.target, entry);
          } else if (!entry.isIntersecting && onExit) {
            onExit(entry.target, entry);
          }
        });
      };

      const observer = this.create(callback, options);
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (target) observer.observe(target);
      
      return observer;
    }
  },

  /**
   * URL & Query Parameter Utilities
   * Simplified methods for working with URLs and query parameters
   */
  URL: {
    /**
     * Get single query parameter value
     * @param {string} name - Parameter name
     * @param {string} url - URL to parse (default: current URL)
     * @returns {string|null}
     */
    getParam(name, url = window.location.search) {
      const params = new URLSearchParams(url);
      return params.get(name);
    },

    /**
     * Build query string from object or FormData
     * @param {Object|FormData} data - Object or FormData with key-value pairs
     * @returns {string} - Query string
     */
    buildQuery(data) {
      if (data instanceof FormData) {
        return new URLSearchParams(Array.from(data)).toString();
      }
      
      const params = new URLSearchParams();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(key, value);
        }
      });
      return params.toString();
    },

    /**
     * Update URL with query string (adds to browser history)
     * @param {string} queryString - Query string to set
     * @param {Object} options - Options {state, path}
     */
    updateUrl(queryString, options = {}) {
      const { state = {}, path = window.location.pathname } = options;
      const newUrl = queryString ? `${path}?${queryString}` : path;
      window.history.pushState(state, '', newUrl);
    },

    /**
     * Replace current URL without adding to browser history
     * @param {string} queryString - Query string to set
     * @param {Object} options - Options {state, path}
     */
    replaceUrl(queryString, options = {}) {
      const { state = {}, path = window.location.pathname } = options;
      const newUrl = queryString ? `${path}?${queryString}` : path;
      window.history.replaceState(state, '', newUrl);
    },

    /**
     * Remove hash from URL without adding to browser history
     * @param {Object} options - Options {state, keepQuery}
     */
    removeHash(options = {}) {
      const { state = {}, keepQuery = true } = options;
      const path = window.location.pathname;
      const search = keepQuery ? window.location.search : '';
      const newUrl = `${path}${search}`;
      window.history.replaceState(state, '', newUrl);
    }
  },

  /**
   * Animation & Transition Utilities
   * Simplified methods for common animation patterns
   */
  Animate: {
    /**
     * Internal fade effect implementation
     * @private
     * @param {Element} elem - Element to animate
     * @param {string} action - 'fadeIn' or 'fadeOut'
     */
    _fadeEffect(elem, action) {
      const FADE_STEP = 0.01;

      if (action === 'fadeIn') {
        elem.style.display = 'block';
        elem.style.opacity = 0;
        const fadeIn = () => {
          let newValue = parseFloat(elem.style.opacity);
          if (newValue < 1) {
            newValue += FADE_STEP;
            elem.style.opacity = newValue;
            requestAnimationFrame(fadeIn);
          } else {
            elem.style.opacity = 1;
          }
        };
        requestAnimationFrame(fadeIn);
      } else if (action === 'fadeOut') {
        elem.style.opacity = 1;
        const fadeOut = () => {
          let newValue = parseFloat(elem.style.opacity);
          if (newValue > 0) {
            newValue -= FADE_STEP;
            elem.style.opacity = newValue;
            requestAnimationFrame(fadeOut);
          } else {
            elem.style.opacity = 0;
            elem.style.display = 'none';
          }
        };
        requestAnimationFrame(fadeOut);
      }
    },

    /**
     * Fade in element
     * @param {Element|string} element - Element or selector
     * @param {Function} callback - Callback when fade completes (optional)
     */
    fadeIn(element, callback) {
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (!target) return;
      
      this._fadeEffect(target, 'fadeIn');
      if (callback) {
        callback();
      }
    },

    /**
     * Fade out element
     * @param {Element|string} element - Element or selector
     * @param {Function} callback - Callback when fade completes (optional)
     */
    fadeOut(element, callback) {
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (!target) return;
      
      this._fadeEffect(target, 'fadeOut');
      if (callback) {
        callback();
      }
    },

    /**
     * Show message with fade in/out effect
     * @param {Element|string} element - Element or selector
     * @param {number} duration - How long to show message in ms (default: 3000)
     */
    showMessage(element, duration = 3000) {
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (!target) return;
      
      this.fadeIn(target, () => {
        setTimeout(() => {
          this.fadeOut(target);
        }, duration);
      });
    },

    /**
     * Smooth class change using requestAnimationFrame
     * @param {Element|string} element - Element or selector
     * @param {Function} updateFn - Function to execute on next frame
     */
    smooth(element, updateFn) {
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (!target) return;
      
      requestAnimationFrame(() => updateFn(target));
    },

    /**
     * Set loading state on element
     * @param {Element|string} element - Element or selector
     * @param {boolean} isLoading - Loading state
     */
    setLoading(element, isLoading) {
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (!target) return;

      if (isLoading) {
        target.setAttribute('aria-disabled', true);
        target.classList.add('loading');
      } else {
        target.classList.remove('loading');
        target.removeAttribute('aria-disabled');
      }
    },

    /**
     * Vibrate mobile device for haptic feedback
     * @param {number|Array<number>} pattern - Vibration duration in ms or pattern (default: 100)
     * @example
     * Utility.Animate.vibrate(); // Single 100ms vibration
     * Utility.Animate.vibrate(200); // Single 200ms vibration
     * Utility.Animate.vibrate([100, 50, 100]); // Pattern: vibrate, pause, vibrate
     */
    vibrate(pattern = 100) {
      if (navigator.vibrate) {
        navigator.vibrate(pattern);
      }
    }
  },

   /**
   * Focus Management Utilities
   * ADA-compliant focus management for modals, drawers, and interactive elements
   */
  Focus: {
    _handlers: {},
    _activeContainer: null,
    _previouslyFocused: null,
    _forceTimeout: null,

    /**
     * Check if element is visible and focusable
     * @private
     */
    _isVisible(element) {
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    },

    /**
     * Handle arrow key navigation between focusable elements
     * @param {KeyboardEvent} event - Keyboard event
     * @param {Element} container - Container to navigate within
     * @param {boolean} includeHidden - Include hidden elements
     * @returns {boolean} True if navigation was handled
     */
    handleArrowKeys(event, container, includeHidden = false) {
      if (!container) return false;
      
      const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
      if (!arrowKeys.includes(event.key)) return false;
      if (!container.contains(document.activeElement)) return false;
      
      const focusableElements = this.getFocusableElements(container, includeHidden);
      if (!focusableElements.length) return false;
      
      event.preventDefault();
      
      const currentIndex = focusableElements.findIndex(el => el === document.activeElement || el.contains(document.activeElement));
      let nextIndex = currentIndex;
      
      if (currentIndex === -1) {
        // No element currently focused, start from first
        nextIndex = 0;
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
      }
      
      if (nextIndex >= 0 && nextIndex < focusableElements.length) {
        focusableElements[nextIndex].focus();
        return true;
      }
      
      return false;
    },

    /**
     * Get all focusable elements within a container
     * @param {Element} container
     * @param {boolean} includeHidden - Include hidden elements
     * @returns {Array<Element>}
     */
    getFocusableElements(container, includeHidden = false) {
      if (!container) {
        console.warn('Focus.getFocusableElements: No container provided');
        return [];
      }

      const selector = [
        // Custom theme elements
        '.filter__container',
        // Links
        'a[href]',
        'a[data-href]',
        // Buttons and inputs
        '[type="button"]',
        'button:enabled',
        'input:not([type=hidden]):not([tabindex^="-"]):enabled',
        'select:enabled',
        'textarea:enabled',
        // Interactive elements
        'summary',
        '[contenteditable]:not([contenteditable="false"])',
        'details',
        // Media with controls
        'audio[controls]',
        'video[controls]',
        // Embedded content
        'embed',
        'object',
        'iframe',
        // Map area
        'area[href]',
        // Custom focusable
        '[tabindex]:not([tabindex^="-"])',
        '[draggable="true"]'
      ].join(', ');

      const elements = Array.from(container.querySelectorAll(selector));

      return elements.filter(el => {
        if (el.getAttribute('aria-hidden') === 'true' || el.hasAttribute('inert')) return false;
        return includeHidden || this._isVisible(el);
      });
    },

    /**
     * Force focus on an element
     * @param {Element} element
     * @param {object} options - {preventScroll, timeout}
     * @returns {boolean} Success status
     */
    force(element, options = {}) {
      const { preventScroll = false, timeout = 500 } = options;

      if (!element || !this._isVisible(element)) {
        console.warn('Focus.force: Invalid or invisible element', element);
        return false;
      }

      if (this._forceTimeout) clearTimeout(this._forceTimeout);

      element.focus({ preventScroll });
      if (element === document.activeElement) return true;

      // Retry with tabindex
      const hadTabindex = element.hasAttribute('tabindex');
      const originalTabindex = element.getAttribute('tabindex');
      element.setAttribute('tabindex', '0');
      
      this._forceTimeout = setTimeout(() => {
        element.focus({ preventScroll });
        if (!hadTabindex) {
          element.removeAttribute('tabindex');
        } else if (originalTabindex !== '0') {
          element.setAttribute('tabindex', originalTabindex);
        }
        if (element !== document.activeElement) {
          console.warn('Focus.force: Unable to focus after retry');
        }
      }, timeout);

      return element === document.activeElement;
    },

    trap(container, elementToFocus = null, options = {}) {
      const { returnFocus = true, skipVisibilityCheck = false, includeHidden = false, enableArrowKeys = false } = options;

      if (!container) return false;
      if (!skipVisibilityCheck && !this._isVisible(container)) {
        requestAnimationFrame(() => setTimeout(() => this.trap(container, elementToFocus, { ...options, skipVisibilityCheck: true, includeHidden: true }), 50));
        return false;
      }

      const checkHidden = includeHidden || skipVisibilityCheck;
      const elements = this.getFocusableElements(container, checkHidden);
      if (!elements.length) { container.setAttribute('tabindex', '-1'); return false; }

      if (returnFocus && document.activeElement !== document.body) this._previouslyFocused = document.activeElement;
      this.release(null, false);
      this._activeContainer = container;

      this._handlers.keydown = (e) => {
        // Tab key navigation (existing behavior)
        if (e.key === 'Tab' || e.keyCode === 9) {
          const current = this.getFocusableElements(container, checkHidden);
          if (!current.length || !container.contains(document.activeElement)) return;
          const [first, last] = [current[0], current[current.length - 1]];
          if ((document.activeElement === last && !e.shiftKey) || (document.activeElement === first && e.shiftKey)) {
            e.preventDefault();
            (document.activeElement === last ? first : last).focus();
          }
          return;
        }

        // Arrow key navigation (optional, for menus/dropdowns)
        if (enableArrowKeys) {
          this.handleArrowKeys(e, container, checkHidden);
        }
      };

      this._handlers.focusin = (e) => {
        if (this._activeContainer && !this._activeContainer.contains(e.target)) {
          e.preventDefault(); e.stopPropagation();
          this.getFocusableElements(container, checkHidden)[0]?.focus();
        }
      };

      document.addEventListener('keydown', this._handlers.keydown, true);
      document.addEventListener('focusin', this._handlers.focusin, true);
      requestAnimationFrame(() => this.force(elementToFocus || elements[0]));
      return true;
    },

    /**
     * Release focus trap
     * @param {Element} elementToFocus - Element to focus after release
     * @param {boolean} usePrevious - Use stored previous focus
     */
    release(elementToFocus = null, usePrevious = true) {
      // Remove event listeners with capture flag
      ['focusin', 'keydown'].forEach(type => {
        if (this._handlers[type]) {
          document.removeEventListener(type, this._handlers[type], true);
        }
      });

      this._handlers = {};
      this._activeContainer = null;

      if (this._forceTimeout) {
        clearTimeout(this._forceTimeout);
        this._forceTimeout = null;
      }

      const returnElement = elementToFocus || (usePrevious ? this._previouslyFocused : null);
      
      if (returnElement?.isConnected) {
        try {
          returnElement.focus();
        } catch (error) {
          console.warn('Focus.release: Unable to return focus', error);
        }
      }

      this._previouslyFocused = null;
    },

    /** Check if focus is trapped */
    isTrapped() {
      return this._activeContainer !== null;
    },

    /** Get active trapped container */
    getActiveContainer() {
      return this._activeContainer;
    },

    /**
     * Store element identifier for focus restoration after DOM update
     * @param {Element} element - Element to store (defaults to activeElement)
     * @param {Element} container - Optional container to check if element is within
     * @returns {Object|null} Stored element identifier or null
     */
    storeForRestore(element = null, container = null) {
      const activeElement = element || document.activeElement;
      if (!activeElement) return null;
      
      // Check if element is within container if provided
      if (container && !container.contains(activeElement)) return null;
      
      // Store element identifier - prefer ID, fallback to selector
      if (activeElement.id) {
        return { selector: `#${activeElement.id}` };
      }
      if (activeElement.getAttribute('for')) {
        return { selector: `label[for="${activeElement.getAttribute('for')}"]` };
      }
      if (activeElement.href) {
        return { selector: `a[href="${activeElement.href}"]` };
      }
      // Fallback to element reference if still connected
      return activeElement.isConnected ? { element: activeElement } : null;
    },

    /**
     * Restore focus to a previously stored element
     * @param {Object} storedElement - Element identifier from storeForRestore
     * @param {Element} context - Optional context element to search within
     * @returns {boolean} True if focus was restored
     */
    restore(storedElement, context = document) {
      if (!storedElement) return false;
      
      let elementToFocus = null;
      
      if (storedElement.selector) {
        elementToFocus = Utility.DomSelector.$(storedElement.selector, context);
      } else if (storedElement?.element?.isConnected) {
        elementToFocus = storedElement.element;
      }
      
      if (elementToFocus?.isConnected) {
        requestAnimationFrame(() => {
          elementToFocus.focus();
        });
        return true;
      }
      
      return false;
    }
  },

  /**
   * Fetch utilities with error handling and common patterns
   */
   Fetch: {
    /**
     * Fetch with automatic error handling
     * @param {string} url - Request URL
     * @param {object} options - Fetch options
     * @returns {Promise<Response>}
     */
    async request(url, options = {}) {
      try {
        const response = await fetch(url, options);
        if (!response.ok) {
          const error = new Error(`HTTP error! status: ${response.status}`);
          error.status = response.status;
          error.response = response;

          try {
            const contentType = response.headers.get('content-type') || '';
            const isJson = contentType.includes('application/json');
            const data = isJson ? await response.json() : await response.text();
            error.data = data;

            if (isJson) {
              const message = data?.message?.trim() || data?.description?.trim();
              if (message) error.message = message;
            } else if (data?.trim()) {
              error.message = data.trim();
            }
          } catch (parseError) {
            error.parseError = parseError;
          }

          throw error;
        }
        return response;
      } catch (error) {
        console.error('Fetch error:', error);
        const fallback = error?.message || 'Request Failed';
        Utility.handleError(fallback, 'Request Failed');
        throw error;
      }
    },

    /**
     * Fetch JSON data
     * @param {string} url
     * @param {object} options
     * @returns {Promise<object>}
     */
    async json(url, options = {}) {
      const response = await this.request(url, options);
      return response.json();
    },

    /**
     * Fetch HTML text
     * @param {string} url
     * @param {object} options
     * @returns {Promise<string>}
     */
    async html(url, options = {}) {
      const response = await this.request(url, options);
      return response.text();
    },

    /**
     * Fetch Shopify section HTML
     * @param {string} url - Base URL (can include existing query parameters)
     * @param {string|array} sectionIds - Section ID(s)
     * @returns {Promise<object>}
     */
    async section(url, sectionIds) {
      const sections = Array.isArray(sectionIds) ? sectionIds.join(',') : sectionIds;
      const separator = url.includes('?') ? '&' : '?';
      const sectionUrl = `${url}${separator}sections=${sections}`;
      return await this.json(sectionUrl);
    },

    /**
     * Fetch product data by handle
     * @param {string} handle - Product handle
     * @param {string} view - View template (optional)
     * @returns {Promise<string>}
     */
    async product(handle, view = null) {
      const url = view ? `/products/${handle}?view=${view}` : `/products/${handle}`;
      return this.html(url);
    },

    /**
     * Build fetch config for POST requests
     * @param {string} type - request Accept type ('json' or 'javascript')
     * @param {object} config - Additional config (headers, body)
     * @returns {object} Fetch configuration object
     */
    config(type = 'json', config = {}) {
      /** @type {Headers} */
      const headers = { 'Content-Type': 'application/json', Accept: `application/${type}`, ...config.headers };

      if (type === 'javascript') {
        headers['X-Requested-With'] = 'XMLHttpRequest';
        delete headers['Content-Type'];
      }

      return {
        method: 'POST',
        headers: /** @type {HeadersInit} */ (headers),
        body: config.body,
      };
    }
  },

  /**
   * Storage Management Utilities
   * Unified methods for cookies, localStorage, and sessionStorage
   */
  Storage: {
    Cookie: {
      set(name, value, options = {}) {
        const { days = 365, path = '/', domain = '', secure = false, sameSite = 'Lax' } = options;
        const val = typeof value === 'object' ? JSON.stringify(value) : String(value);
        const date = new Date(Date.now() + days * 864e5);
        let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(val)}; expires=${date.toUTCString()}; path=${path}`;
        if (domain) cookie += `; domain=${domain}`;
        if (secure) cookie += '; secure';
        if (sameSite) cookie += `; samesite=${sameSite}`;
        document.cookie = cookie;
        return true;
      },

      get(name, parseJSON = false) {
        const nameEQ = `${encodeURIComponent(name)}=`;
        const cookie = document.cookie.split(';').find(c => c.trim().indexOf(nameEQ) === 0);
        if (!cookie) return null;
        const value = decodeURIComponent(cookie.trim().substring(nameEQ.length));
        return parseJSON ? ((() => { try { return JSON.parse(value); } catch { return value; } })()) : value;
      },

      remove(name, options = {}) {
        return this.set(name, '', { ...options, days: -1 });
      },

      exists(name) {
        return this.get(name) !== null;
      },

      getAll() {
        const cookies = {};
        document.cookie.split(';').forEach(c => {
          const [n, v] = c.trim().split('=');
          if (n) cookies[decodeURIComponent(n)] = decodeURIComponent(v || '');
        });
        return cookies;
      }
    },

    Local: {
      set(key, value, options = {}) {
        try {
          const data = { value, timestamp: Date.now(), ...(options.ttl && { expiry: Date.now() + options.ttl }) };
          localStorage.setItem(key, JSON.stringify(data));
          return true;
        } catch { return false; }
      },

      get(key, defaultValue = null) {
        try {
          const item = localStorage.getItem(key);
          if (!item) return defaultValue;
          const data = JSON.parse(item);
          if (data.expiry && Date.now() > data.expiry) {
            this.remove(key);
            return defaultValue;
          }
          return data.value;
        } catch {
          return localStorage.getItem(key) || defaultValue;
        }
      },

      remove(key) { try { localStorage.removeItem(key); return true; } catch { return false; } },
      clear() { try { localStorage.clear(); return true; } catch { return false; } },
      exists(key) { return localStorage.getItem(key) !== null; },
      keys() { return Object.keys(localStorage); },
      size() { return Object.keys(localStorage).reduce((s, k) => s + k.length + localStorage[k].length, 0); }
    },

    Session: {
      set(key, value) {
        try {
          sessionStorage.setItem(key, JSON.stringify({ value, timestamp: Date.now() }));
          return true;
        } catch { return false; }
      },

      get(key, defaultValue = null) {
        try {
          const item = sessionStorage.getItem(key);
          return item ? JSON.parse(item).value : defaultValue;
        } catch {
          return sessionStorage.getItem(key) || defaultValue;
        }
      },

      remove(key) { try { sessionStorage.removeItem(key); return true; } catch { return false; } },
      clear() { try { sessionStorage.clear(); return true; } catch { return false; } },
      exists(key) { return sessionStorage.getItem(key) !== null; },
      keys() { return Object.keys(sessionStorage); }
    }
  },

  /**
   * DOM Utilities
   * Methods for parsing and manipulating HTML
   */
  DOM: {
    /**
     * Parse HTML string to DOM
     * @param {string} htmlString - HTML string to parse
     * @returns {Document}
     */
    parse(htmlString) {
      return new DOMParser().parseFromString(htmlString, 'text/html');
    },

    /**
     * Update destination element from source HTML
     * @param {string} id - Selector for element to update
     * @param {Document} html - Parsed HTML document
     * @param {Element} container - Target container (default: document)
     */
    update(id, html, container = document) {
      const source = html.querySelector(id);
      const destination = container.querySelector(id);

      if (source && destination) {
        destination.innerHTML = source.innerHTML;
        destination.classList.remove('d-none');
      } else {
        destination?.classList.add('d-none');
      }
    },

    /**
     * Update multiple different selectors from parsed HTML
     * Updates the first match of each selector in the array
     * @param {Array<string>} selectors - Array of selectors to update
     * @param {Document} sourceHTML - Parsed HTML document
     * @param {Element} container - Target container (default: document)
     */
    updateSelectors(selectors, sourceHTML, container = document) {
      selectors.forEach(selector => this.update(selector, sourceHTML, container));
    },

    /**
     * Update all matching elements from parsed HTML (handles NodeList)
     * Updates all elements that match the selector, not just the first one.
     * If there are more destinations than sources, extra destinations will use
     * the first source as a fallback template.
     * @param {string} selector - CSS selector (may match multiple elements)
     * @param {Document} html - Parsed HTML document
     * @param {Element} container - Target container (default: document)
     */
    updateAll(selector, html, container = document) {
      const sources = html.querySelectorAll(selector);
      const destinations = container.querySelectorAll(selector);

      if (destinations.length === 0) {
        return;
      }else if(sources.length === 0) {
        destinations.forEach(dest => dest.classList.add('d-none'));
        return;
      }

      destinations.forEach((destination, index) => {
        const source = sources[index] || sources[0];
        destination.innerHTML = source.innerHTML;
        destination.classList.remove('d-none');
      });
    },

    /**
     * Extract element from HTML string
     * @param {string|Document} htmlString - HTML string or parsed document
     * @param {string} selector - CSS selector
     * @returns {Element|null}
     */
    extract(htmlString, selector) {
      const parsed = typeof htmlString === 'string' ? this.parse(htmlString) : htmlString;
      return parsed.querySelector(selector);
    }
  },

  /**
   * Form Utilities
   * Methods for form handling and validation
   */
  Form: {
    /**
     * Serialize form data
     * @param {HTMLFormElement} form - Target form element
     * @returns {Object} Serialized form data
     */
    serialize(form) {
      if (!(form instanceof HTMLFormElement)) {
        throw new TypeError('Expected a form element');
      }

      const obj = {};
      const formData = new FormData(form);
      const regex = /^properties\[(.*?)\]$/;

      for (const key of formData.keys()) {
        const match = regex.exec(key);
        if (match) {
          obj.properties = obj.properties || {};
          obj.properties[match[1]] = formData.get(key);
        } else {
          obj[key] = formData.get(key);
        }
      }
      return obj;
    },

    /**
     * Validate email format
     * @param {string} email - Email to validate
     * @returns {boolean} Whether email is valid
     */
    validateEmail(email) {
      return !!email.match(/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/);
    },

    /**
     * Validate form and set ARIA attributes
     * @param {HTMLFormElement} form - Form to validate
     * @returns {Array<string>} Array of error messages
     */
    validate(form) {
      const errors = [];
      const requiredFields = form.querySelectorAll('[required]');

      requiredFields.forEach(field => {
        if (!field.value.trim()) {
          field.setAttribute('aria-invalid', 'true');
          field.setAttribute('aria-describedby', `error-${field.id}`);
          errors.push(`${field.getAttribute('aria-label') || field.name} is required`);
        } else {
          field.removeAttribute('aria-invalid');
        }
      });

      return errors;
    }
  },

   /**
   * Cart Management Utilities
   */
  Cart: {
    /** Line item property name for `product.type` (matches Liquid hidden input) */
    LINE_ITEM_PROPERTY_PRODUCT_TYPE: '_product_type',

    /**
     * Cart / checkout order attribute key; value comes from company metafield `custom.tax_exemption` on `customer.current_company` when set.
     */
    ORDER_ATTRIBUTE_TAX_EXEMPTION: 'tax_exemption',

    /**
     * @param {*} v - companyTaxExemption from themeData (company metafield value)
     * @returns {boolean} false for nil, empty string, or whitespace-only
     */
    hasCompanyTaxExemptionOrderValue(v) {
      if (v === null || v === undefined) return false;
      if (typeof v === 'string' && v.trim() === '') return false;
      return true;
    },

    /**
     * String for cart `attributes[tax_exemption]` (order note attributes in checkout)
     * @param {*} v
     * @returns {string}
     */
    stringForOrderAttributeValue(v) {
      if (v === true) return 'true';
      if (v === false) return 'false';
      if (typeof v === 'number' && Number.isFinite(v)) return String(v);
      if (typeof v === 'string') return v;
      try {
        return JSON.stringify(v);
      } catch {
        return String(v);
      }
    },

    /**
     * Mirrors `themeData.companyTaxExemption` into cart attributes.
     * Clears the cart attribute when the company value is absent, including after logout.
     * Skips when cart already has the same value. Updates `themeData.cart.attributes` on success.
     * @returns {Promise<void>}
     */
    async syncCompanyTaxExemptionOrderAttribute() {
      const raw = globalThis.themeData?.companyTaxExemption;
      const key = Utility.Cart.ORDER_ATTRIBUTE_TAX_EXEMPTION;
      const hasDesiredValue = Utility.Cart.hasCompanyTaxExemptionOrderValue(raw);
      const desired = hasDesiredValue ? Utility.Cart.stringForOrderAttributeValue(raw) : '';
      const current = globalThis.themeData?.cart?.attributes?.[key];
      if (!hasDesiredValue && (current === null || current === undefined || current === '')) return;
      if (hasDesiredValue && String(current ?? '') === desired) return;
      const updateUrl = globalThis.routes?.cart_update_url
        ? `${globalThis.routes.cart_update_url}.js`
        : '/cart/update.js';
      const res = await fetch(updateUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { [key]: desired } })
      });
      if (!res.ok) return;
      let cart = null;
      try {
        cart = await res.json();
      } catch {
        return;
      }
      if (globalThis.themeData) {
        globalThis.themeData.cart = globalThis.themeData.cart || {};
        globalThis.themeData.cart.attributes = cart?.attributes
          || { ...globalThis.themeData.cart.attributes, [key]: desired };
      }
    },

    /**
     * Update cart count in header
     */
    updateHeaderCount() {
      const cartIcons = document.querySelectorAll('[data-cart-icon]');
      const count = Number(globalThis.themeData?.cart?.item_count ?? 0);
      cartIcons.forEach(icon => {
        const cartCount =
          icon.querySelector('[data-cart-count]') ||
          icon.parentElement?.querySelector('[data-cart-count]');
        if (cartCount) {
          cartCount.textContent = String(count);
          cartCount.classList.toggle('d-none', count === 0);
        }
      });
    },

    /**
     * Add item to cart
     * @param {object} formData - Form data or item object
     * @returns {Promise<object>}
     */
    async addItem(formData) {
      const config = Utility.Fetch.config('json', {
        body: JSON.stringify(formData)
      });
      
      const response = await fetch(globalThis.routes.cart_add_url, config);
      const data = await response.json();
      
      if (data.status === 422 || data.description) {
        throw new Error(data.description || 'Unable to add to cart');
      }

      return data;
    },

    /**
     * Line item properties for Shopify product `type` (merchant-defined, e.g. Pro / Retail).
     * @param {object} product - themeData.product or Storefront product JSON
     * @returns {Object|null} e.g. { _product_type: 'Pro' } or null if absent
     */
    lineItemPropertiesFromProductType(product) {
      const type = product?.type;
      if (typeof type !== 'string' || !type.trim()) return null;
      return { [Utility.Cart.LINE_ITEM_PROPERTY_PRODUCT_TYPE]: type.trim() };
    },

    /**
     * Ensures cart/add FormData includes product type from hidden input, data-product-type, or themeData.product.
     * @param {FormData} formData
     * @param {HTMLElement} [productFormHost] - <product-form> element
     */
    ensureProductTypeLineItemPropertyInFormData(formData, productFormHost) {
      if (!(formData instanceof FormData) || !productFormHost) return;
      const key = `properties[${Utility.Cart.LINE_ITEM_PROPERTY_PRODUCT_TYPE}]`;
      const existing = formData.get(key);
      if (existing && String(existing).trim()) return;
      const fromDataset = productFormHost.dataset?.productType?.trim();
      const pid = productFormHost.dataset?.productid;
      const tp = globalThis.themeData?.product;
      const fromTheme =
        tp && pid && String(pid) === String(tp.id) && typeof tp.type === 'string'
          ? tp.type.trim()
          : '';
      const val = fromDataset || fromTheme;
      if (val) formData.set(key, val);
    }
  },

  /**
    * Limit the number of times a function is called within defined timeout
    *
    * @param {function} fn - function to execute
    * @param {integer} wait - Timeout to fire event
    * @returns {Function} Debounced function with optional cancel() method
  */
  debounce(fn, wait) {
    let t;
    const debounced = (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
    debounced.cancel = () => {
      clearTimeout(t);
      t = null;
    };
    return debounced;
  },

  /**
   * Format date
   * @param  {string}
   * @return {String} value - formatted value
   */
  formatDate(value) {
    const date = new Date(value);
    const ye = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
    const mo = new Intl.DateTimeFormat('en', { month: 'short' }).format(date);
    const da = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
    return `${mo} ${da} ${ye}`;
  },

  /**
   * Get swatch hex color code or image url
   *
   * @param {string} colorName
   * @returns {string} background style hex code
  */
  getSwatchStyle(colorName) {
    if (!this._swatchesCache) {
      const swatchesElement = Utility.DomSelector.$('[data-swatches-colorlist-json]');
      if (swatchesElement) {
        try {
          this._swatchesCache = JSON.parse(swatchesElement.innerText);
        } catch (e) {
          console.warn('Failed to parse swatches color list', e);
          this._swatchesCache = {}; // Prevent repeated errors
        }
      } else {
        this._swatchesCache = {}; // Element not found, prevent repeated queries
      }
    }
    
    if (!this._swatchesCache || Object.keys(this._swatchesCache).length === 0) {
      return false;
    }

    colorName = colorName.replace(/-|\s/g, '').toLowerCase();
    const swatch = this._swatchesCache[colorName];
    let swatchStyle;
    if (typeof swatch !== 'undefined') {
      if (swatch.match(/\.(jpeg|jpg|png|gif)/g) != null) {
        swatchStyle = `background-image: url(${swatch}); background-size: cover; background-position: center;`;
      } else {
        swatchStyle = `background-color: ${swatch}`;
      }
      return swatchStyle;
    }
    return false;
  },

  /**
  * Toggle element visibility
  * @param {Element} container - Container element
  * @param {string} activity - Activity to toggle
  * @returns {void}
   */
  toggleElement(container, activity) {
    const contentBlock = container.querySelector('[data-type="content"]') || container.querySelector('.toggle-content');
    if(!contentBlock) return;

    if(activity == 'open'){
      Utility.CSS.add([contentBlock, container], 'open');
      contentBlock.removeAttribute('inert');
      contentBlock.setAttribute('aria-hidden', 'false');
    }else{
      Utility.CSS.remove([contentBlock, container], 'open');
      contentBlock.setAttribute('inert', '');
      contentBlock.setAttribute('aria-hidden', 'true');
    }
  },

  /**
   * Live Region Manager - Persistent ARIA live regions for screen reader announcements
   * @private
   */
  _liveRegions: {},

  /**
   * Get or create live region
   * @private
   */
  _getLiveRegion(priority) {
    if (!this._liveRegions[priority]) {
      const region = document.createElement('div');
      region.setAttribute('aria-live', priority);
      region.setAttribute('aria-atomic', 'true');
      region.className = 'visually-hidden';
      document.body.appendChild(region);
      this._liveRegions[priority] = region;
    }
    return this._liveRegions[priority];
  },

  /**
   * Announce message to screen readers
   * @param {string} message - Message to announce
   * @param {string} priority - 'polite' or 'assertive' (default: 'polite')
   */
  announceToScreenReader(message, priority = 'polite') {
    const region = this._getLiveRegion(priority);
    
    // Clear previous message
    region.textContent = '';
    
    // Set new message after a brief delay (ensures screen readers detect change)
    setTimeout(() => {
      region.textContent = message;
    }, 100);
  },

  /**
   * Format money values based on your shop currency settings
   * @param  {Number|string} cents - value in cents or dollar amount
   * @return {String} value - formatted value
   */
  formatMoney(cents, format) {
    format = format || globalThis.themeData.money_format || "${{ amount }}";
    if (typeof cents === 'string') cents = cents.replace('.', '');
    
    const formatWithDelimiters = (num, prec = 2, thou = ',', dec = '.') => {
      if (isNaN(num) || num == null) return 0;
      const fixed = (num / 100).toFixed(prec);
      const parts = fixed.split('.');
      return parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thou}`) + (parts[1] ? dec + parts[1] : '');
    };

    const type = format.match(/\{\{\s*(\w+)\s*\}\}/)[1];
    const value = {
      'amount': formatWithDelimiters(cents, 2),
      'amount_no_decimals': formatWithDelimiters(cents, 0),
      'amount_with_comma_separator': formatWithDelimiters(cents, 2, '.', ','),
      'amount_no_decimals_with_comma_separator': formatWithDelimiters(cents, 0, '.', ',')
    }[type];

    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  },

  /**
   * Extract error message from various error formats
   * Handles Shopify API errors, fetch errors, and generic errors
   * @param {Error|Object|string} error - Error object or message
   * @returns {string} Extracted error message
   */
  extractErrorMessage(error) {
    // Handle string errors
    if (typeof error === 'string') {
      return error.trim() || 'An error occurred';
    }

    // Handle error objects with data property (Shopify API format)
    if (error?.data) {
      const data = typeof error.data === 'string' 
        ? (() => {
            try {
              return JSON.parse(error.data);
            } catch {
              return { message: error.data };
            }
          })()
        : error.data;

      return data?.message || data?.description || data?.error || data?.errors || 'An error occurred';
    }

    // Handle error objects with message property
    if (error?.message) {
      return error.message;
    }

    // Fallback
    return 'An error occurred';
  },

  handleError(e, title = 'Error') {
    const message = this.extractErrorMessage(e);
    window.notificationEle.updateNotification(title, message, {
      type: 'error',
      timeout: 15000
    });
  },

  handleSuccess(e, title = 'Success') {
    window.notificationEle.updateNotification(title, e, {
      type: 'success',
      timeout: 15000
    });
  }
};

globalThis.Utility = Utility;

(function initCompanyOrderAttributes() {
  if (typeof document === 'undefined' || !Utility.Cart?.syncCompanyTaxExemptionOrderAttribute) return;
  const run = () => {
    Utility.Cart.syncCompanyTaxExemptionOrderAttribute().catch(() => {});
  };
  const debounced = typeof Utility.debounce === 'function' ? Utility.debounce(run, 120) : run;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  if (typeof eventBus !== 'undefined' && typeof THEME_EVENTS !== 'undefined') {
    eventBus.subscribe(THEME_EVENTS.cartUpdate, debounced);
  }
})();
